import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ArrowLeft, Monitor, Smartphone, Tablet, Clock, Activity, 
  Globe, AlertCircle, Loader2, Play, Info
} from 'lucide-react';
import 'rrweb-player/dist/style.css';

const API_ENDPOINT = 'https://clients.causalfunnels.com/api';

// Event type names
const EVENT_TYPES = {
  0: '🎬 DOM Content Loaded',
  1: '🔄 Incremental Snapshot',
  2: '📸 Full Snapshot',
  3: '⚡ Meta',
  4: '🎨 Custom',
  5: '🖱️ Interaction',
};

const SessionReplayPlayer = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [sessionInfo, setSessionInfo] = useState({
    shortId: '',
    startTime: '',
    duration: '',
    eventCount: 0,
    deviceType: 'desktop'
  });
  
  const playerRef = useRef(null);
  const replayerRef = useRef(null);

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const user = urlParams.get('user') || 'causalfunnel';
  const sessionId = urlParams.get('sessionId');
  const date = urlParams.get('date') || new Date().toISOString().split('T')[0];

  // Detect device type
  const detectDeviceType = useCallback((userInfo) => {
    const ua = (userInfo?.userAgent || '').toLowerCase();
    const viewport = userInfo?.viewportSize || '';
    const [width] = viewport.split('x').map(v => parseInt(v));

    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
      return 'mobile';
    }
    if (/tablet|ipad|playbook|silk/i.test(ua)) {
      return 'tablet';
    }
    if (width && width < 768) {
      return 'mobile';
    }
    if (width && width < 1024) {
      return 'tablet';
    }
    return 'desktop';
  }, []);

  // Detect browser
  const detectBrowser = (ua) => {
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edge')) return 'Edge';
    if (ua.includes('Opera')) return 'Opera';
    return 'Unknown';
  };

  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Format duration
  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Get player dimensions
  const getPlayerDimensions = useCallback((metadata) => {
    const deviceType = detectDeviceType(metadata.userInfo);
    const viewport = metadata.userInfo?.viewportSize || '1024x768';
    const [originalWidth, originalHeight] = viewport.split('x').map(v => parseInt(v));

    let width, height;

    // Calculate available space (accounting for sidebar ~320px and padding)
    const availableWidth = Math.max(window.innerWidth - 450, 600);
    const availableHeight = Math.max(window.innerHeight - 200, 500);

    // Use original viewport dimensions
    width = originalWidth;
    height = originalHeight;

    // Constrain to reasonable limits
    width = Math.min(width, availableWidth);
    height = Math.min(height, availableHeight);

    // Maintain aspect ratio when scaling down
    const aspectRatio = originalWidth / originalHeight;
    if (width / height > aspectRatio) {
      width = Math.floor(height * aspectRatio);
    } else {
      height = Math.floor(width / aspectRatio);
    }

    // Ensure minimum sizes
    width = Math.max(width, 320);
    height = Math.max(height, 400);

    return { width, height, deviceType };
  }, [detectDeviceType]);

  // Load session
  const loadSession = useCallback(async () => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    try {
      const url = `${API_ENDPOINT}/session/${user}/${sessionId}${date ? `?date=${date}` : ''}`;
      console.log('Fetching session from:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Session not found (HTTP ${response.status})`);
      }

      const data = await response.json();
      console.log('Session response:', data);
      
      if (!data.events || data.events.length === 0) {
        throw new Error('No events found in session');
      }

      setSessionData(data);
      
      // Set session info
      const shortId = sessionId.split('_')[2] || sessionId;
      const duration = data.metadata.lastUpdate - data.metadata.startTime;
      const deviceType = detectDeviceType(data.metadata.userInfo);
      
      setSessionInfo({
        shortId,
        startTime: formatDate(data.metadata.startTime),
        duration: formatDuration(duration),
        eventCount: data.metadata.eventCount,
        deviceType,
        metadata: data.metadata
      });

      setLoading(false);
    } catch (err) {
      console.error('Error loading session:', err);
      setError(err.message || 'Failed to load session');
      setLoading(false);
    }
  }, [sessionId, user, date, detectDeviceType]);

  // Initialize player
  const initializePlayer = useCallback(async () => {
    if (!sessionData || !playerRef.current) return;

    try {
      // Dynamically import rrweb-player
      const rrwebPlayer = (await import('rrweb-player')).default;
      
      const dimensions = getPlayerDimensions(sessionData.metadata);
      
      // Clear previous player
      if (replayerRef.current) {
        replayerRef.current.destroy();
      }
      playerRef.current.innerHTML = '';

      // Suppress console warnings from rrweb
      const originalWarn = console.warn;
      console.warn = (...args) => {
        if (args[0]?.includes?.('[replayer]')) return;
        originalWarn.apply(console, args);
      };
      
      replayerRef.current = new rrwebPlayer({
        target: playerRef.current,
        props: {
          events: sessionData.events,
          width: dimensions.width,
          height: dimensions.height,
          autoPlay: true,
          showController: true,
          speedOption: [1, 2, 4, 8],
          tags: {},
          skipInactive: true,
          showWarning: false, // Suppress warnings in console
          mouseTail: {
            duration: 1000,
            lineCap: 'round',
            lineWidth: 3,
            strokeStyle: '#667eea'
          },
          UNSAFE_replayCanvas: true, // Enable canvas replay
          triggerFocus: false, // Prevent focus issues
          insertStyleRules: [
            // Add fallback styles for missing fonts
            `@font-face {
              font-family: 'eicons';
              font-display: swap;
            }`,
            `@font-face {
              font-family: 'elementskit';
              font-display: swap;
            }`
          ]
        },
      });

      console.log('Player initialized with', sessionData.events.length, 'events');
      
      // Add error handler for the replayer
      if (replayerRef.current?.replayer) {
        replayerRef.current.replayer.on('error', (error) => {
          // Silently handle replay errors to prevent console spam
          console.debug('Replay error (suppressed):', error);
        });
      }
    } catch (err) {
      console.error('Error initializing player:', err);
      setError('Failed to initialize player: ' + err.message);
    }
  }, [sessionData, getPlayerDimensions]);

  // Load session on mount
  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // Initialize player when data is loaded
  useEffect(() => {
    if (sessionData && !loading && !error) {
      initializePlayer();
    }
  }, [sessionData, loading, error, initializePlayer]);

  // Device icon component
  const DeviceIcon = ({ type }) => {
    switch(type) {
      case 'mobile':
        return <Smartphone className="w-5 h-5" />;
      case 'tablet':
        return <Tablet className="w-5 h-5" />;
      default:
        return <Monitor className="w-5 h-5" />;
    }
  };

  // Jump to event
  const jumpToEvent = (timestamp) => {
    if (replayerRef.current && sessionData) {
      const eventTime = timestamp - sessionData.metadata.startTime;
      replayerRef.current.play(eventTime);
    }
  };

  // Get device badge color
  const getDeviceBadgeColor = (type) => {
    switch(type) {
      case 'mobile':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'tablet':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 font-medium">Loading session replay...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Failed to Load Session</h2>
          <p className="text-gray-400">{error}</p>
          <a
            href="session-replay"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 shadow-xl">
        <div className="max-w-full px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <a
                href="session-replay"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all font-semibold"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </a>
              <div>
                <h1 className="text-xl font-bold">Session {sessionInfo.shortId}</h1>
                <p className="text-purple-100 text-sm">{sessionInfo.startTime}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${getDeviceBadgeColor(sessionInfo.deviceType)}`}>
                <DeviceIcon type={sessionInfo.deviceType} />
                <span className="font-semibold capitalize">{sessionInfo.deviceType}</span>
              </div>
              <div className="px-3 py-2 bg-white/20 rounded-lg font-semibold text-sm">
                {sessionInfo.eventCount} events
              </div>
              <div className="px-3 py-2 bg-white/20 rounded-lg font-semibold text-sm">
                {sessionInfo.duration}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row h-[calc(100vh-88px)]">
        {/* Sidebar */}
        <aside className="w-full lg:w-80 bg-gray-900/50 border-r border-gray-700 overflow-y-auto flex-shrink-0">
          {/* Session Info */}
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Session Info
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Session ID</span>
                <code className="text-sm text-purple-400 font-semibold">{sessionInfo.shortId}</code>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Start Time</span>
                <span className="text-sm text-gray-200">{sessionInfo.startTime}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Duration</span>
                <span className="text-sm text-gray-200">{sessionInfo.duration}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Events</span>
                <span className="text-sm text-gray-200">{sessionInfo.eventCount}</span>
              </div>
            </div>
          </div>

          {/* Device Info */}
          {sessionData?.metadata?.userInfo && (
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                Device Info
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Platform</span>
                  <span className="text-sm text-gray-200">{sessionData.metadata.userInfo.platform || 'Unknown'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Screen</span>
                  <span className="text-sm text-gray-200">{sessionData.metadata.userInfo.screenResolution || 'Unknown'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Viewport</span>
                  <span className="text-sm text-gray-200">{sessionData.metadata.userInfo.viewportSize || 'Unknown'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Browser</span>
                  <span className="text-sm text-gray-200">{detectBrowser(sessionData.metadata.userInfo.userAgent || '')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Pages Visited */}
          {sessionData?.metadata?.pages && sessionData.metadata.pages.length > 0 && (
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Pages Visited
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Array.from(new Map(sessionData.metadata.pages.map(p => [p.url, p])).values()).map((page, idx) => (
                  <div key={idx} className="p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors">
                    <div className="text-sm font-semibold text-gray-200 mb-1 truncate">
                      {page.title || 'Untitled'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{page.url}</div>
                    <div className="text-xs text-gray-600 mt-1">{formatDate(page.timestamp)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Event Timeline */}
          {sessionData?.events && (
            <div className="p-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Event Timeline
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {sessionData.events
                  .filter(e => [2, 3, 5].includes(e.type))
                  .slice(0, 50)
                  .map((event, idx) => {
                    const eventTime = event.timestamp - sessionData.metadata.startTime;
                    return (
                      <button
                        key={idx}
                        onClick={() => jumpToEvent(event.timestamp)}
                        className="w-full p-3 bg-gray-800/50 rounded-lg hover:bg-gray-700 transition-all text-left"
                      >
                        <div className="text-xs text-gray-500 mb-1">
                          +{formatDuration(eventTime)}
                        </div>
                        <div className="text-sm text-gray-200 font-medium">
                          {EVENT_TYPES[event.type] || `Event ${event.type}`}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </aside>

        {/* Player Area */}
        <main className="flex-1 bg-gray-800/30 p-4 overflow-hidden relative">
          {/* Player Container */}
          <div className="flex items-center justify-center h-full w-full p-4">
            <div
              ref={playerRef}
              className={`
                ${sessionInfo.deviceType === 'mobile' ? 'rounded-3xl border-[12px] border-gray-700 shadow-2xl' : ''}
                ${sessionInfo.deviceType === 'tablet' ? 'rounded-2xl border-[10px] border-gray-700 shadow-2xl' : ''}
                ${sessionInfo.deviceType === 'desktop' ? 'rounded-lg border-2 border-gray-700 shadow-2xl' : ''}
              `}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default SessionReplayPlayer;